import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Profile } from '@/types/profile';
import { ArrowRight, Flame, Star, Trophy, CheckCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { format, isToday, isTomorrow, isFuture, differenceInDays, differenceInHours } from 'date-fns';
import { getTimezoneAbbr, parseDateTimeString } from '@/utils/dateUtils';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

interface ScheduledMatch {
  id: string;
  match_date: string;
  location?: string;
}

interface ActivePoll {
  id: string;
  title: string;
  group_id: string;
}

interface UpcomingTournament {
  id: string;
  name: string;
  group_id: string;
  proposed_date: string;
  start_time?: string;
  status: string;
  isParticipating?: boolean;
}

interface PlayerStats {
  currentStreak?: number;
  currentStreakType?: 'win' | 'loss' | 'draw';
  winRate: number;
  totalMatches: number;
}

interface BannerSlide {
  message: string;
  icon: React.ElementType;
  ctaText: string;
  ctaAction: () => void;
  priority: number;
}

interface WelcomeBannerProps {
  profile: Profile;
  scheduledMatches: ScheduledMatch[];
  activePolls: ActivePoll[];
  upcomingTournaments?: UpcomingTournament[];
  stats?: PlayerStats;
  pendingValidations?: number;
  showSkillPrompt?: boolean;
  onSkillPrompt?: () => void;
  /** True while the underlying dashboard queries are still in flight. */
  loading?: boolean;
}

export const WelcomeBanner: React.FC<WelcomeBannerProps> = ({
  profile,
  scheduledMatches,
  activePolls,
  upcomingTournaments = [],
  stats,
  pendingValidations = 0,
  showSkillPrompt = false,
  onSkillPrompt,
  loading = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('welcome.goodMorning');
    if (hour < 17) return t('welcome.goodAfternoon');
    return t('welcome.goodEvening');
  };

  const slides = useMemo(() => {
    const slidesList: BannerSlide[] = [];

    const upcomingMatches = scheduledMatches
      .filter(m => isFuture(parseDateTimeString(m.match_date)))
      .sort((a, b) => parseDateTimeString(a.match_date).getTime() - parseDateTimeString(b.match_date).getTime());
    
    const nextMatch = upcomingMatches[0];
    
    if (nextMatch) {
      const matchDate = parseDateTimeString(nextMatch.match_date);
      const daysUntil = differenceInDays(matchDate, new Date());
      
      const tzAbbr = getTimezoneAbbr(matchDate);
      const fmtTime = (d: Date) => `${format(d, 'h:mm a')} ${tzAbbr}`;
      let timeText = '';
      if (isToday(matchDate)) {
        timeText = t('welcome.today', { time: fmtTime(matchDate) });
      } else if (isTomorrow(matchDate)) {
        timeText = t('welcome.tomorrow', { time: fmtTime(matchDate) });
      } else if (daysUntil <= 7) {
        timeText = t('welcome.onDay', { day: format(matchDate, 'EEEE'), time: fmtTime(matchDate) });
      } else {
        timeText = t('welcome.onDate', { date: format(matchDate, 'MMM d'), time: fmtTime(matchDate) });
      }
      
      slidesList.push({
        message: t('welcome.matchScheduled', { time: timeText }),
        icon: Trophy,
        ctaText: t('welcome.viewMatch'),
        ctaAction: () => navigate(ROUTES.MATCHES),
        priority: 1,
      });
    }

    if (pendingValidations > 0) {
      slidesList.push({
        message: t('welcome.pendingValidations', { count: pendingValidations }),
        icon: CheckCircle,
        ctaText: t('welcome.review'),
        ctaAction: () => navigate(ROUTES.MATCHES),
        priority: 2,
      });
    }

    if (activePolls.length > 0) {
      slidesList.push({
        message: t('welcome.pollsWaiting', { count: activePolls.length }),
        icon: Clock,
        ctaText: t('welcome.respond'),
        ctaAction: () => {
          const firstPoll = activePolls[0];
          navigate(`${ROUTES.GROUP_DETAIL(firstPoll.group_id)}?tab=polls`);
        },
        priority: 3,
      });
    }

    const participatingTournaments = upcomingTournaments.filter(t => t.isParticipating && t.status !== 'completed');
    if (participatingTournaments.length > 0) {
      const nextTournament = participatingTournaments[0];
      const tournamentDate = new Date(nextTournament.proposed_date);
      const daysUntil = differenceInDays(tournamentDate, new Date());
      const timeStr = nextTournament.start_time 
        ? nextTournament.start_time.substring(0, 5)
        : null;
      
      let timeText = '';
      if (isToday(tournamentDate)) {
        timeText = timeStr ? t('welcome.today', { time: timeStr }) : t('dates.today').toLowerCase();
      } else if (isTomorrow(tournamentDate)) {
        timeText = timeStr ? t('welcome.tomorrow', { time: timeStr }) : t('dates.yesterday').toLowerCase();
      } else if (daysUntil <= 7) {
        timeText = timeStr 
          ? t('welcome.onDay', { day: format(tournamentDate, 'EEEE'), time: timeStr })
          : format(tournamentDate, 'EEEE');
      } else {
        timeText = timeStr
          ? t('welcome.onDate', { date: format(tournamentDate, 'MMM d'), time: timeStr })
          : format(tournamentDate, 'MMM d');
      }
      
      slidesList.push({
        message: t('welcome.tournamentStarts', { name: nextTournament.name, time: timeText }),
        icon: Trophy,
        ctaText: t('welcome.viewTournament'),
        ctaAction: () => navigate(`/groups/${nextTournament.group_id}/tournaments/${nextTournament.id}`),
        priority: 3.5,
      });
    }

    const availableTournaments = upcomingTournaments.filter(t => !t.isParticipating && t.status === 'open');
    availableTournaments.forEach((tournament, index) => {
      slidesList.push({
        message: t('welcome.tournamentOpen', { name: tournament.name }),
        icon: Trophy,
        ctaText: t('welcome.registerNow'),
        ctaAction: () => navigate(`/groups/${tournament.group_id}/tournaments/${tournament.id}`),
        priority: 3.7 + (index * 0.01),
      });
    });

    if (showSkillPrompt && onSkillPrompt) {
      slidesList.push({
        message: t('welcome.skillAssessment'),
        icon: Star,
        ctaText: t('welcome.start'),
        ctaAction: onSkillPrompt,
        priority: 4,
      });
    }

    if (stats?.currentStreak && stats.currentStreak >= 3) {
      let messageKey = 'welcome.winningStreak';
      if (stats.currentStreakType === 'loss') messageKey = 'welcome.losingStreak';
      else if (stats.currentStreakType === 'draw') messageKey = 'welcome.drawStreak';
      
      slidesList.push({
        message: t(messageKey, { count: stats.currentStreak }),
        icon: Flame,
        ctaText: t('welcome.viewStats'),
        ctaAction: () => navigate(ROUTES.PROFILE),
        priority: 5,
      });
    }

    if (stats && stats.totalMatches >= 5 && stats.winRate > 60 && !(stats?.currentStreak && stats.currentStreak >= 3)) {
      slidesList.push({
        message: t('welcome.winRate', { rate: stats.winRate }),
        icon: Star,
        ctaText: t('welcome.viewStats'),
        ctaAction: () => navigate(ROUTES.PROFILE),
        priority: 6,
      });
    }

    if (slidesList.length === 0) {
      slidesList.push({
        message: t('welcome.allCaughtUp'),
        icon: Trophy,
        ctaText: t('welcome.findPlayers'),
        ctaAction: () => navigate(ROUTES.MATCHES),
        priority: 10,
      });
    }

    return slidesList.sort((a, b) => a.priority - b.priority);
  }, [scheduledMatches, stats, activePolls, pendingValidations, showSkillPrompt, onSkillPrompt, navigate, t, upcomingTournaments]);

  const statsChip = useMemo(() => {
    if (!stats || stats.totalMatches === 0) return null;
    if (stats.totalMatches >= 3) {
      return {
        icon: Star,
        text: `${Math.round(stats.winRate)}% WR`,
        color: stats.winRate > 60 ? 'text-yellow-400' : 'text-white/90',
      };
    }
    return null;
  }, [stats]);

  const renderSlide = (slide: BannerSlide, showStatsChip: boolean) => {
    const MessageIcon = slide.icon;
    
    return (
      <div className="relative z-10 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white/90 font-medium mb-1">
              {getGreeting()}, {profile.full_name?.split(' ')[0]}
            </p>
            <div className="flex items-center gap-2">
              <MessageIcon className="h-5 w-5 flex-shrink-0" />
              <p className="text-base font-semibold leading-tight">
                {slide.message}
              </p>
            </div>
          </div>

          {showStatsChip && statsChip && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 flex-shrink-0"
            >
              <statsChip.icon className={`h-4 w-4 ${statsChip.color}`} />
              <span className="text-sm font-semibold whitespace-nowrap">
                {statsChip.text}
              </span>
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-3"
        >
          <Button
            onClick={slide.ctaAction}
            variant="secondary"
            size="sm"
            className="bg-white/90 hover:bg-white text-padel-blue-light font-semibold shadow-md transition-all hover:shadow-lg"
          >
            {slide.ctaText}
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        </motion.div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative px-4 py-4 mb-4 mx-4 mt-2 rounded-xl overflow-hidden shadow-lg"
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-padel-blue-light to-padel-green-light"
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear',
        }}
        style={{
          backgroundSize: '200% 200%',
        }}
      />

      {loading ? (
        <div className="relative z-10 text-white">
          <p className="text-sm text-white/90 font-medium mb-2">
            {getGreeting()}, {profile.full_name?.split(' ')[0]}
          </p>
          <div className="h-5 w-2/3 rounded bg-white/25 animate-pulse" />
          <div className="mt-3 h-8 w-32 rounded-md bg-white/25 animate-pulse" />
        </div>
      ) : slides.length === 1 ? (
        renderSlide(slides[0], true)
      ) : (
        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          plugins={[
            Autoplay({
              delay: 5000,
              stopOnInteraction: true,
              stopOnMouseEnter: true,
            }) as any,
          ]}
          className="w-full"
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index}>
                {renderSlide(slide, index === 0)}
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="relative z-10 mt-3 flex justify-center">
            <CarouselDots className="gap-1.5" />
          </div>
        </Carousel>
      )}
    </motion.div>
  );
};
